import { NgModule } from '@angular/core';
import {
  ApolloClientOptions,
  InMemoryCache,
  NormalizedCacheObject,
  concat,
} from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import {
  createOktaAuthLink,
  createSubscriptionRoutingLink,
  getOktaAuthorizationHeaders,
  SSELink,
} from '@sc/data-access-graphql';
import { APOLLO_OPTIONS, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { environment } from '../environments/environment';
import { IFrameEventManagerService } from '@sc/feature-script-manager';

const { subBaseUrlhttp: subhttp_uri } = environment.api;
const http_uri = '/api/gql';

export function createApollo(
  httpLink: HttpLink,
  iframeEventService: IFrameEventManagerService
): ApolloClientOptions<NormalizedCacheObject> {
  const http = httpLink.create({
    uri: http_uri,
  });

  const http_onsub = httpLink.create({
    uri: subhttp_uri,
  });

  const link = createSubscriptionRoutingLink(
    new SSELink({
      url: subhttp_uri,
      headers: getOktaAuthorizationHeaders,
    }),
    http_onsub,
    http
  );

  const authMiddleware = createOktaAuthLink();

  // 👇 ajout du errorLink
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ extensions }) => {
        iframeEventService.sendClientError(graphQlStatus(extensions));
      });
    }

    function graphQlStatus(extensions: Record<string, unknown>): string {
      const exception = extensions['exception'];
      if (exception && typeof exception === 'object' && 'data' in exception) {
        const data = exception.data;
        if (data && typeof data === 'object' && 'status' in data) {
          return String(data.status);
        }
      }
      return String(extensions['code'] ?? '500');
    }

    if (networkError) {
      iframeEventService.sendClientError(
        'status' in networkError ? (networkError.status as string) : '500'
      );
    }
  });

  return {
    link: concat(authMiddleware, concat(errorLink, link)),
    cache: new InMemoryCache({
      addTypename: false,
    }),
  };
}

@NgModule({
  imports: [ApolloModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, IFrameEventManagerService],
    },
  ],
})
export class GraphQLModule {}
